import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import axios from "axios";
import { ThemeProvider } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import businessFlowsTheme from "../../theme/businessFlowsTheme";
import { PasswordField, StrengthMeter } from "../auth/AuthControls";
import { Hint, Refusal } from "../workspaces/WorkspaceUi";
import "../auth/auth.css";

const MIN_LENGTH = 8;

export default function ChangePassword({ user }) {
  const email = user?.email || "";
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setResult(null);
    const found = {};
    if (!current) found.current = "Enter your current password.";
    if (next.length < MIN_LENGTH) found.next = `Use at least ${MIN_LENGTH} characters.`;
    else if (next === current) found.next = "Choose a password you haven't used here.";
    if (confirm !== next) found.confirm = "The passwords don't match.";
    setErrors(found);
    if (Object.keys(found).length) return;

    setBusy(true);
    try {
      const response = await axios.post(
        "/neoPublic/change-password",
        { email: email.toLowerCase(), oldPassword: current, newPassword: next },
        { headers: { "auth-token": localStorage.getItem("auth") } }
      );
      const ok = Boolean(response.data?.success);
      setResult({ success: ok, msg: response.data?.msg });
      if (ok) {
        setCurrent("");
        setNext("");
        setConfirm("");
      }
    } catch (err) {
      setResult({
        success: false,
        msg: err?.response?.data?.msg || "We couldn't change your password. Check your current password and try again.",
      });
    }
    setBusy(false);
  };

  return (
    <ThemeProvider theme={businessFlowsTheme}>
      <Box
        className="bfx"
        sx={{
          boxSizing: "border-box",
          minHeight: "100%",
          bgcolor: "background.default",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          px: 2,
          py: { xs: 2, sm: 4 },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 480 }}>
          <Link
            component={RouterLink}
            to="/my-account"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mb: 2, fontWeight: 500 }}
          >
            <ArrowBackRoundedIcon fontSize="small" /> My account
          </Link>
          <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 3.5 }, borderRadius: 4 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 3,
                bgcolor: "primary.main",
                color: "secondary.main",
                display: "grid",
                placeItems: "center",
                mb: 2,
              }}
            >
              <LockResetRoundedIcon />
            </Box>
            <Typography variant="h5" component="h1" sx={{ mb: 0.5 }}>
              Change password
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              You'll stay logged in on this device after changing it.
            </Typography>

            {result &&
              (result.success ? (
                <Hint sx={{ mb: 3 }}>Your password has been changed.</Hint>
              ) : (
                <Refusal sx={{ mb: 3 }}>{result.msg}</Refusal>
              ))}

            <Box component="form" onSubmit={submit} noValidate>
              <Stack spacing={2}>
                <TextField
                  label="Account email"
                  value={email}
                  InputProps={{ readOnly: true }}
                  autoComplete="username"
                />
                <PasswordField
                  label="Current password"
                  autoComplete="current-password"
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  error={Boolean(errors.current)}
                  helperText={errors.current}
                />
                <Box>
                  <PasswordField
                    label="New password"
                    autoComplete="new-password"
                    value={next}
                    onChange={(e) => setNext(e.target.value)}
                    error={Boolean(errors.next)}
                    helperText={errors.next || `At least ${MIN_LENGTH} characters. Longer is stronger.`}
                  />
                  <StrengthMeter value={next} />
                </Box>
                <PasswordField
                  label="Confirm new password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  error={Boolean(errors.confirm)}
                  helperText={errors.confirm}
                />
                <LoadingButton type="submit" variant="contained" size="large" loading={busy} fullWidth>
                  Update password
                </LoadingButton>
              </Stack>
            </Box>
          </Paper>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
